<template>
    <div>
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="$t('product.info.create_schedule')"
            :edit-mode-text="$t('product.info.edit_schedule')"
            @reset="editableSchedule = null"
        >
            <template v-slot:btn-block>
                <div class="full-width d-flex align-center">
                    <v-spacer />
                    <FilterStatusToggle v-model="activeFilter" />
                </div>
            </template>
            <template v-slot:main-mode>
                <v-data-table-server
                    :headers="useHeaders"
                    :items="useFilteredScheduleList"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :loading="isLoading"
                    :items-length="useFilteredScheduleList.length"
                    :custom-key-sort="{
                        name: (a, b) => localized(a).localeCompare(localized(b)),
                    }"
                    :sort-by="[{key: 'name', order: 'asc'}]"
                    must-sort
                    density="compact"
                    color="primary"
                    class="elevation-1 table-server"
                    data-cy="charter-schedule-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="text-link" @click="switchToEditMode(item)">
                            {{ localized(item.name) }}
                        </div>
                    </template>
                    <template v-slot:item.active="{item}">
                        {{ item.active ? $t('active') : $t('inactive') }}
                    </template>
                </v-data-table-server>
            </template>
            <template v-slot:additional-mode>
                <InfoCharterScheduleForm
                    :editable-schedule="editableSchedule"
                    :contract-id="contractId"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import InfoCharterScheduleForm from '~/components/products/information/InfoCharterScheduleForm.vue'

    const localized = useLocalized()

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const scheduleList = ref<any[]>([])
    const modeSwitcher = ref(null)
    const editableSchedule = ref<number>(null)
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId, hidden: false}
            const {services} = await app.$api.ownCharterServices.get(rq)
            scheduleList.value = services
        } catch (e) {
            scheduleList.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('flight_number'), value: 'name', minWidth: '120px', sortable: true},
            {title: t('flights.departure_time'), value: 'startTime', minWidth: '120px'},
            {title: t('flights.arrival_time'), value: 'endDate', minWidth: '120px'},
            {title: t('duration'), value: 'duration', minWidth: '120px'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const switchToEditMode = (schedule): void => {
        modeSwitcher.value.edit()
        editableSchedule.value = schedule
    }

    const useFilteredScheduleList = computed(() => {
        return scheduleList.value.filter(({active}) => activeFilter.value === active)
    })
</script>
